// Multi-step staff login page.
// State machine: idle → webauthn | totp → done

import { useNavigate } from '@tanstack/react-router';
import React, { useEffect, useRef, useState } from 'react';
import { Button, Input, Field, ErrorChip } from '@awakenedlands/ui';
import { apiFetch } from '@/lib/api';
import { b64urlToUint8Array, serializeAssertionResponse } from '@/lib/webauthn';

// Minimal types for the WebAuthn options coming back from the server
interface WebAuthnAllowCredential {
  id: string;
  type: string;
  transports?: AuthenticatorTransport[];
}

interface WebAuthnOptions {
  challenge: string;
  timeout?: number;
  rpId?: string;
  allowCredentials: WebAuthnAllowCredential[];
  userVerification?: UserVerificationRequirement;
}

type LoginStep = 'idle' | 'webauthn' | 'totp';

// Augment window to include optional Turnstile
declare global {
  interface Window {
    turnstile?: {
      render: (
        selector: string,
        options: { sitekey: string; callback: (token: string) => void },
      ) => string;
      reset: (widgetId: string) => void;
    };
  }
}

export function LoginPage() {
  const navigate = useNavigate();

  // Form state
  const [loginName, setLoginName] = useState('');
  const [password, setPassword] = useState('');
  const [turnstileToken, setTurnstileToken] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const [backupCode, setBackupCode] = useState('');
  const [useBackupCode, setUseBackupCode] = useState(false);

  // Flow state
  const [step, setStep] = useState<LoginStep>('idle');
  const [challengeId, setChallengeId] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const turnstileWidgetRef = useRef<string | null>(null);

  // Load Turnstile script once
  useEffect(() => {
    if (document.querySelector('script[data-turnstile]')) return;
    const script = document.createElement('script');
    script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js';
    script.async = true;
    script.defer = true;
    script.dataset['turnstile'] = '1';
    document.head.appendChild(script);
  }, []);

  // Render Turnstile widget after script loads
  useEffect(() => {
    if (step !== 'idle') return;

    const sitekey = import.meta.env['VITE_TURNSTILE_SITE_KEY'] as string | undefined;
    if (!sitekey) return;

    const tryRender = () => {
      if (window.turnstile) {
        turnstileWidgetRef.current = window.turnstile.render('#turnstile-container', {
          sitekey,
          callback: setTurnstileToken,
        });
      }
    };

    // Script may already be loaded
    tryRender();

    const interval = setInterval(() => {
      if (window.turnstile) {
        clearInterval(interval);
        tryRender();
      }
    }, 200);

    return () => clearInterval(interval);
  }, [step]);

  // ── WebAuthn sub-flow ──────────────────────────────────────────────────────

  async function doWebAuthn(cid: string): Promise<void> {
    setLoading(true);
    setError('');

    try {
      // 1. Get challenge options from server
      const optRes = await apiFetch<{ options: WebAuthnOptions }>(
        '/admin/auth/webauthn/options',
        { method: 'POST', body: JSON.stringify({ challenge_id: cid }) },
      );

      if (!optRes.ok) {
        setError('WebAuthn challenge failed. Please try again.');
        setLoading(false);
        return;
      }

      const opts = optRes.data.options;

      // 2. Invoke native browser WebAuthn
      const allowCredentials: PublicKeyCredentialDescriptor[] =
        opts.allowCredentials.map((c) => {
          const desc: PublicKeyCredentialDescriptor = {
            id: b64urlToUint8Array(c.id).buffer as ArrayBuffer,
            type: c.type as PublicKeyCredentialType,
          };
          if (c.transports !== undefined) {
            desc.transports = c.transports;
          }
          return desc;
        });

      const publicKeyOpts: PublicKeyCredentialRequestOptions = {
        challenge: b64urlToUint8Array(opts.challenge).buffer as ArrayBuffer,
        allowCredentials,
        ...(opts.timeout !== undefined && { timeout: opts.timeout }),
        ...(opts.rpId !== undefined && { rpId: opts.rpId }),
        ...(opts.userVerification !== undefined && {
          userVerification: opts.userVerification,
        }),
      };

      const credential = (await navigator.credentials.get({
        publicKey: publicKeyOpts,
      })) as PublicKeyCredential | null;

      if (!credential) {
        setError('WebAuthn cancelled or not available.');
        setLoading(false);
        return;
      }

      // 3. Send assertion to server
      const verifyRes = await apiFetch<{ next?: string; challenge_id?: string }>(
        '/admin/auth/webauthn/verify',
        {
          method: 'POST',
          body: JSON.stringify({
            challenge_id: cid,
            assertion_response: serializeAssertionResponse(credential),
          }),
        },
      );

      if (verifyRes.ok) {
        void navigate({ to: '/dashboard' });
        return;
      }

      // 4. Server may indicate TOTP fallback
      if (verifyRes.data.next === 'totp') {
        const newChallengeId = verifyRes.data.challenge_id ?? cid;
        // Get fresh challenge for TOTP
        const fallbackRes = await apiFetch<{ challenge_id: string }>(
          '/admin/auth/fallback',
          { method: 'POST', body: JSON.stringify({ challenge_id: cid, target_factor: 'totp' }) },
        );
        setChallengeId(fallbackRes.ok ? fallbackRes.data.challenge_id : newChallengeId);
        setStep('totp');
      } else {
        setError('WebAuthn verification failed. Please try again.');
      }
    } catch {
      setError('WebAuthn error. Please try again or use a backup code.');
    } finally {
      setLoading(false);
    }
  }

  // ── Step 1: Password submit ────────────────────────────────────────────────

  async function handlePasswordSubmit(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    setError('');

    const res = await apiFetch<{
      requires?: string;
      challenge_id?: string;
      message?: string;
    }>('/admin/auth/login', {
      method: 'POST',
      body: JSON.stringify({
        login_name: loginName,
        password,
        turnstile_token: turnstileToken,
      }),
    });

    if (!res.ok) {
      setError(res.data.message ?? 'Invalid credentials.');
      // Reset Turnstile widget
      if (window.turnstile && turnstileWidgetRef.current) {
        window.turnstile.reset(turnstileWidgetRef.current);
        setTurnstileToken('');
      }
      setLoading(false);
      return;
    }

    const cid = res.data.challenge_id ?? '';
    setChallengeId(cid);

    if (res.data.requires === 'webauthn') {
      setStep('webauthn');
      await doWebAuthn(cid);
    } else if (res.data.requires === 'totp') {
      setStep('totp');
      setLoading(false);
    } else {
      // Session established without MFA step
      void navigate({ to: '/dashboard' });
    }
  }

  // ── Step 2: TOTP submit ───────────────────────────────────────────────────

  async function handleTotpSubmit(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    setError('');

    const body = useBackupCode
      ? { challenge_id: challengeId, backup_code: backupCode }
      : { challenge_id: challengeId, totp_code: totpCode };

    const res = await apiFetch<{ message?: string }>('/admin/auth/totp', {
      method: 'POST',
      body: JSON.stringify(body),
    });

    if (res.ok) {
      void navigate({ to: '/dashboard' });
      return;
    }

    setError(res.data.message ?? 'Invalid code. Please try again.');
    setLoading(false);
  }

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className="flex min-h-dvh items-center justify-center bg-background p-4">
      <div className="w-full max-w-sm space-y-6 rounded-sm border border-border bg-panel p-8 shadow-sm">
        <div className="space-y-1 text-center">
          <h1 className="text-xl font-semibold text-ink-high">Awakened Lands · Admin</h1>
          <p className="text-sm text-ink-low">Staff sign-in required</p>
        </div>

        {/* ── Password step ── */}
        {step === 'idle' && (
          <form onSubmit={(e) => void handlePasswordSubmit(e)} className="space-y-4">
            <Field label="Login name">
              <Input
                id="login_name"
                name="login_name"
                type="text"
                autoComplete="username"
                required
                value={loginName}
                onChange={(e) => setLoginName(e.target.value)}
                disabled={loading}
              />
            </Field>

            <Field label="Password">
              <Input
                id="password"
                name="password"
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={loading}
              />
            </Field>

            {/* Turnstile challenge */}
            <div id="turnstile-container" />

            {error && <ErrorChip>{error}</ErrorChip>}

            <Button type="submit" width="full" disabled={loading}>
              {loading ? (
                <span className="flex items-center gap-2">
                  <span className="h-4 w-4 animate-spin rounded-full border-2 border-primary-foreground border-t-transparent" />
                  Signing in…
                </span>
              ) : (
                'Sign in'
              )}
            </Button>
          </form>
        )}

        {/* ── WebAuthn in-progress ── */}
        {step === 'webauthn' && (
          <div className="space-y-4">
            <p className="text-sm text-ink-mid">
              Complete the security key prompt to continue…
            </p>
            {loading && (
              <div className="flex justify-center">
                <div className="h-8 w-8 animate-spin rounded-full border-4 border-ink-low border-t-primary" />
              </div>
            )}
            {error && <ErrorChip>{error}</ErrorChip>}
          </div>
        )}

        {/* ── TOTP step ── */}
        {step === 'totp' && (
          <form onSubmit={(e) => void handleTotpSubmit(e)} className="space-y-4">
            <p className="text-sm text-ink-mid">
              {useBackupCode
                ? 'Enter a backup code.'
                : 'Enter the 6-digit code from your authenticator app.'}
            </p>

            {!useBackupCode ? (
              <Field label="Authenticator code">
                <Input
                  id="totp_code"
                  name="totp_code"
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]{6}"
                  maxLength={6}
                  autoComplete="one-time-code"
                  required
                  value={totpCode}
                  onChange={(e) => setTotpCode(e.target.value)}
                  disabled={loading}
                />
              </Field>
            ) : (
              <Field label="Backup code">
                <Input
                  id="backup_code"
                  name="backup_code"
                  type="text"
                  autoComplete="off"
                  required
                  value={backupCode}
                  onChange={(e) => setBackupCode(e.target.value)}
                  disabled={loading}
                />
              </Field>
            )}

            <button
              type="button"
              className="text-xs text-ink-low underline hover:text-ink-mid"
              onClick={() => {
                setUseBackupCode(!useBackupCode);
                setTotpCode('');
                setBackupCode('');
                setError('');
              }}
            >
              {useBackupCode ? 'Use authenticator code instead' : 'Use a backup code'}
            </button>

            {error && <ErrorChip>{error}</ErrorChip>}

            <Button type="submit" width="full" disabled={loading}>
              {loading ? (
                <span className="flex items-center gap-2">
                  <span className="h-4 w-4 animate-spin rounded-full border-2 border-primary-foreground border-t-transparent" />
                  Verifying…
                </span>
              ) : (
                'Verify'
              )}
            </Button>
          </form>
        )}
      </div>
    </div>
  );
}
